import { ImageName } from "@mcleod/images";
import { DesignerInterface, PanelProps, PropType } from "@mcleod/components";
import { ActionPermissionSelector } from "./ActionPermissionSelector";
import { ColorSelector } from "./ColorSelector";
import { ContextMenuEditor } from "./ContextMenuEditor";
import { DropdownItemEditor } from "./DropdownItemEditor";
import { HelpLinkEditor } from "./HelpLinkEditor";
import { ImageSelector } from "./ImageSelector";
import { LayoutUseSelector } from "./LayoutUseSelector";
import { MultiLineEditor } from "./MultiLineEditor";
import { PanelOpenFieldList } from "./PanelOpenFieldList";
import { PanelOpenLayout } from "./PanelOpenLayout";
import { PanelOpenModel } from "./PanelOpenModel";
import { RadioBoxItemEditor } from "./RadioBoxItemEditor";
import { RadioGroupItemEditor } from "./RadioGroupItemEditor";
import { SpecialtyPropertyEditor } from "./SpecialtyPropertyEditor";
import { StringArrayEditor } from "./StringArrayEditor";
import { TableExtraSortFieldsEditor } from "./TableExtraSortFieldsEditor";
import { TailorExtensionEditor } from "./TailorExtensionEditor";

export class SpecialtyEditorDef {
    private _propType: PropType;
    private _textboxEnabled: boolean;
    private _buttonImage: ImageName;

    constructor(propType: PropType, textboxEnabled: boolean = false, buttonImage: ImageName = ImageName.menuEllipsis) {
        this._propType = propType;
        this._textboxEnabled = textboxEnabled;
        this._buttonImage = buttonImage;
    }

    public get propType(): PropType {
        return this._propType;
    }

    public get textboxEnabled(): boolean {
        return this._textboxEnabled;
    }

    public get buttonImage(): ImageName {
        return this._buttonImage;
    }

    construct(textboxText: string, editingDesigner: DesignerInterface, panelProps?: Partial<PanelProps>):
        SpecialtyPropertyEditor {
        switch (this.propType) {
            case PropType.decoratorType:
                return new LayoutUseSelector(textboxText, panelProps);
            case PropType.helpLink:
                return new HelpLinkEditor(textboxText, panelProps);
            case PropType.layout:
                return new PanelOpenLayout(textboxText, panelProps);
            case PropType.fieldList:
                return new PanelOpenFieldList(textboxText, panelProps);
            case PropType.endpoint:
                return new PanelOpenModel(textboxText, panelProps);
            case PropType.tailorExtension:
                return new TailorExtensionEditor(textboxText, panelProps);
            case PropType.image:
                return new ImageSelector(panelProps);
            case PropType.color:
                return new ColorSelector(panelProps);
            case PropType.stringArray:
                return new StringArrayEditor(textboxText, panelProps);
            case PropType.multilineString:
                return new MultiLineEditor(textboxText, panelProps);
            case PropType.dropdownItem:
                return new DropdownItemEditor(textboxText, editingDesigner, panelProps);
            case PropType.radioBoxItem:
                return new RadioBoxItemEditor(textboxText, editingDesigner, panelProps);
            case PropType.radioButtonGroupItem:
                return new RadioGroupItemEditor(textboxText, editingDesigner, panelProps);
            case PropType.actionPermission:
                return new ActionPermissionSelector(textboxText, panelProps);
            case PropType.contextMenu:
                return new ContextMenuEditor(editingDesigner, panelProps);
            case PropType.tableExtraSortFields: {
                const fieldsInTableDataSource =
                    TableExtraSortFieldsEditor.gatherFieldsForTableDataSource(editingDesigner);
                return new TableExtraSortFieldsEditor(textboxText, panelProps, fieldsInTableDataSource);
            }
            default:
                throw new Error("Unexpected editor type");
        }
    }
}
